import Callout from '@/components/common/Callout';
import { FlexCol } from '@/components/common/Flex';
import { Lock01 } from '@/components/common/icons';
import InputField from '@/components/common/InputField';
import { Paragraph } from '@/components/common/Typography';
import { useVGSContext } from '@/components/common/VGSProvider';
import IdvIntro from '@/components/idv/IdvIntro';
import { useTrackPageViewed } from '@/services/analytics/useTracking';
import { useUserData } from '@/services/user';
import { assert } from '@common/errors';
import { TAX_RESIDENT_OPTIONS } from '@common/inputs';
import { Country, ResidenceStatus } from '@common/types';
import { useState } from 'react';
import { IdvForm, IdvFormType } from '../IdvForm';

type T = IdvFormType<{
    country?: Country;
    residenceStatus?: ResidenceStatus;
    taxIdToken?: string;
}>;

export const CitizenshipView = () => {
    useTrackPageViewed('IDV > CitizenshipView');

    const { userInfo } = useUserData();
    const [country, setCountry] = useState<Country | undefined>(
        userInfo?.onboardingData?.country || undefined
    );
    const [residenceStatus, setResidenceStatus] = useState<
        ResidenceStatus | undefined
    >(userInfo?.onboardingData?.residenceStatus || undefined);
    const [ssnErrorMessage, setSSNErrorMessage] = useState('');
    const { tokenizeSSN } = useVGSContext();
    const isNonResident =
        residenceStatus &&
        residenceStatus !== 'CITIZEN' &&
        residenceStatus !== 'RESIDENT';

    return (
        <>
            <IdvIntro
                title="What is your citizenship?"
                desc="We use this information to verify your identity and to understand your tax filing status."
            />
            <IdvForm<T>
                initValues={(userProfile) => ({
                    country,
                    residenceStatus:
                        userProfile?.onboardingData?.residenceStatus ||
                        undefined,
                })}
                toMutationInput={(values) => ({
                    taxIdToken: values.taxIdToken,
                    onboardingData: {
                        country: values.country,
                        residenceStatus: values.residenceStatus,
                    },
                })}
                beforeSubmit={async (values) => {
                    assert(tokenizeSSN);
                    const { errorMessage, taxIdToken } = await tokenizeSSN();

                    if (errorMessage) {
                        setSSNErrorMessage(errorMessage);
                        return false;
                    }

                    setSSNErrorMessage('');
                    return { ...values, taxIdToken };
                }}
                backNextNavProps={{
                    isDisabled: isNonResident ? true : undefined,
                    onClickEventParams: {
                        location: 'IDV > CitizenshipView',
                    },
                }}
            >
                <IdvForm.Row>
                    <IdvForm.Input<T>
                        name="country"
                        label="What country are you a citizen of?"
                        placeholder="Select a country"
                        type="country"
                        onChange={(value, updateValues) => {
                            // If user changing to/from USA, reset residence.
                            // Otherwise, leave residence as-is.
                            // N.B. We use "null" here to indicate "no change".
                            const newResidenceStatus =
                                // Changed *to* USA
                                value === 'USA'
                                    ? 'CITIZEN'
                                    : // Changed *from* USA
                                      country === 'USA'
                                      ? undefined
                                      : // No change to/from USA
                                        null;
                            if (newResidenceStatus !== null) {
                                updateValues?.({
                                    residenceStatus: newResidenceStatus,
                                });
                                setResidenceStatus(newResidenceStatus);
                            }

                            setCountry(value);
                            setSSNErrorMessage('');
                        }}
                        autoFocus
                        isRequired
                    />
                </IdvForm.Row>

                {!!country && country !== 'USA' && (
                    <IdvForm.Row>
                        <IdvForm.Input<T>
                            flexGrow={1}
                            flexBasis={0}
                            name="residenceStatus"
                            label={`What is your tax status in the US?`}
                            placeholder="Select a tax status"
                            type="select"
                            onChange={(value, updateValues) => {
                                setResidenceStatus(value);
                                setSSNErrorMessage('');
                                updateValues?.({ residenceStatus: value });
                            }}
                            options={TAX_RESIDENT_OPTIONS}
                            isRequired
                        />
                        <Paragraph
                            flexGrow={1}
                            flexBasis={0}
                            display="flex"
                            color="gray"
                            fontSize="sm"
                        >
                            {residenceStatus === 'RESIDENT'
                                ? 'You intend to file taxes in the United States this year.'
                                : residenceStatus
                                  ? 'You do not intend to file taxes in the United States this year.'
                                  : ''}
                        </Paragraph>
                    </IdvForm.Row>
                )}

                {!!country &&
                    (country === 'USA' || residenceStatus === 'RESIDENT') && (
                        <IdvForm.Row alignItems="flex-start">
                            <FlexCol flexGrow={1} flexBasis={0}>
                                <InputField
                                    label="Social Security Number"
                                    name="ssn"
                                    type="ssn"
                                    placeholder="Enter your SSN"
                                    onChange={() => null}
                                    error={ssnErrorMessage}
                                />
                            </FlexCol>
                            <Paragraph
                                flexGrow={1}
                                flexBasis={0}
                                display="flex"
                                color="gray"
                                fontSize="sm"
                                mt={5}
                            >
                                <Lock01 ml={-7} mr={2} />
                                We use bank-level AES-256 encryption to protect
                                your data.
                            </Paragraph>
                        </IdvForm.Row>
                    )}

                {isNonResident && (
                    <Callout mt={8} iconName="InfoCircle">
                        We’re sorry - we can only support US tax residents at
                        this time.
                    </Callout>
                )}
            </IdvForm>
        </>
    );
};
